import { useTranslate } from 'src/locales';
import { useMemo, useState, useEffect, useCallback } from 'react';
import { hasTimesheetClockings } from '@heyjob-dash/shared/data/timesheetTypes';
import { autoAvailable } from '@heyjob-dash/shared/fringe-auto/auto-feature';
import { getCompanyMenuVersion } from '@heyjob-dash/shared/config/company-menu-version';
import { hasSupervisorPermission, supervisorProfileAllows, isExpensesOnlySupervisor } from '@heyjob-dash/shared/utils/check-permission';
import { MODULE_FLAGS, PREVIEW_FLAGS, isModuleEnabled, isPreviewEnabled, getCurrentCompany } from '@heyjob-dash/shared/config/company-modules';

import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { paths } from 'src/routes/paths';
import { usePathname } from 'src/routes/hooks';

import Label from 'src/components/label';
import SvgColor from 'src/components/svg-color';

import { HighlightDot, HighlightParentDot } from 'src/sections/guides/highlights/highlight-dot';

import Iconify from '../../components/iconify';
import GeneralService from '../../services/GeneralService';
import CompanyService from '../../services/CompanyService';
import { buildCompanyNavV2 } from './config-navigation-v2';
import { isOnboardingCompany, lockNavForOnboarding } from './onboarding-nav';
import { countPendingCompanySignatureDocuments } from '../../utils/signature-documents';

// ----------------------------------------------------------------------

const icon = (name) => (
  <SvgColor
    src={`/assets/icons/navbar/${name}.svg`}
    sx={{ width: 1, height: 1 }}
  />
  // OR
  // <Iconify icon="fluent:mail-24-filled" />
  // https://icon-sets.iconify.design/solar/
  // https://www.streamlinehq.com/icons
);

const ICONS = {
  job: icon('ic_job'),
  timesheet: icon('ic_blog'),
  chat: icon('ic_chat'),
  mail: icon('ic_mail'),
  user: icon('ic_user'),
  file: icon('ic_file'),
  lock: icon('ic_lock'),
  tour: icon('ic_tour'),
  order: icon('ic_order'),
  label: icon('ic_label'),
  blank: icon('ic_blank'),
  kanban: icon('ic_kanban'),
  folder: icon('ic_folder'),
  banking: icon('ic_banking'),
  booking: icon('ic_booking'),
  invoice: icon('ic_invoice'),
  product: icon('ic_product'),
  calendar: icon('ic_calendar'),
  disabled: icon('ic_disabled'),
  external: icon('ic_external'),
  menuItem: icon('ic_menu_item'),
  ecommerce: icon('ic_ecommerce'),
  analytics: icon('ic_analytics'),
  dashboard: icon('ic_dashboard')
};

// ----------------------------------------------------------------------

export function useNavData(user) {
  const { t } = useTranslate();

  const [links, setLinks] = useState([]);
  const [pendingSignatureCount, setPendingSignatureCount] = useState(0);

  const pathname = usePathname();
  const onboarding = isOnboardingCompany(user);

  const fetchLinks = async () => {
    if (user?.data?.companies && user?.data?.current_company && Object.keys(user?.data?.companies).length > 0 && user?.data?.companies[user?.data?.current_company].payroll_id) {
      const res = await GeneralService.getExternalLinks(user.data.companies[user.data.current_company].payroll_id);
      setLinks(res.data);
    }
  }

  useEffect(() => {
    if (!onboarding && user && user?.data?.company && user?.data?.groups.includes('Azienda')) {
      fetchLinks().then()
    }
  }, [user, onboarding]);

  // Il conteggio firme chiama un endpoint che richiede documents_access: senza
  // quel permesso non lo interroghiamo a ogni cambio pagina.
  const canSeeDocuments = hasSupervisorPermission(user, 'documents_access');

  const fetchPendingSignatureCount = useCallback(async () => {
    const companyId = user?.data?.current_company;
    if (!companyId || onboarding || !canSeeDocuments) {
      setPendingSignatureCount(0);
      return;
    }

    try {
      const res = await CompanyService.getSignatureDocuments(companyId);
      setPendingSignatureCount(countPendingCompanySignatureDocuments(res.data?.data));
    } catch {
      setPendingSignatureCount(0);
    }
  }, [user?.data?.current_company, onboarding, canSeeDocuments]);

  useEffect(() => {
    fetchPendingSignatureCount();
  }, [fetchPendingSignatureCount, pathname]);

  const theme = useTheme();

  const smUp = useMediaQuery(theme.breakpoints.up('sm'));

  const currentCompany = getCurrentCompany(user);
  const module = (flag) => isModuleEnabled(currentCompany, flag);
  const preview = (flag) => isPreviewEnabled(currentCompany, flag);

  const legacyData = useMemo(
    () => [
      // OVERVIEW
      // ----------------------------------------------------------------------
      {
        items: [
          ...(isExpensesOnlySupervisor(user) ? [] : [{
            title: t('dashboard'),
            path: paths.dashboard.root,
            icon: ICONS.dashboard
          }]),
          ...((user && (user?.groups?.includes('Azienda') || hasSupervisorPermission(user, 'registry_access'))) ? [{
            title: t('employees'),
            path: paths.dashboard.employees,
            icon: ICONS.user
          }] : []),
          // PRESENZE — aggrega timesheet + richieste + pianificazione ferie
          ...(() => {
            const canAccess = user && (user?.groups?.includes('Azienda') || user?.groups?.includes('Responsabile') || user?.groups?.includes('Timesheet'));
            if (!canAccess) return [];
            const company = user?.data?.current_company ? user?.data?.companies?.[user?.data?.current_company] : undefined;
            const presenzeChildren = [
              ...((smUp && module(MODULE_FLAGS.TIMESHEET) && (hasSupervisorPermission(user, 'timesheet_view') || user?.groups?.includes('Timesheet'))) ? [{
                title: t('timesheet'),
                path: paths.dashboard.timesheet,
              }] : []),
              ...((company?.feature_requests
                && (hasSupervisorPermission(user, 'requests_view') || user?.groups?.includes('Timesheet'))) ? [{
                title: t('requests'),
                path: paths.dashboard.requests,
              }] : []),
              // Il piano ferie legge le richieste: per il Responsabile serve requests_view.
              ...((company?.feature_holiday_plan && supervisorProfileAllows(user, 'requests_view')) ? [{
                // textTransform:none così la "e" resta minuscola (il menu di default applica capitalize)
                title: <span style={{ textTransform: 'none' }}>{t('holiday_plan')}</span>,
                path: paths.dashboard.holidayPlan,
              }] : []),
              ...(user?.groups?.includes('Azienda') && module(MODULE_FLAGS.PROJECTS) ? [{
                title: t('projects'),
                path: paths.dashboard.config.projects,
              }] : []),
            ];
            if (presenzeChildren.length === 0) return [];
            return [{
              title: t('attendance'),
              // Il genitore punta al primo figlio visibile: senza timesheet_view la
              // rotta timesheet è bloccata dal guard e il click porterebbe in dashboard.
              path: presenzeChildren[0].path,
              icon: ICONS.timesheet,
              children: presenzeChildren,
            }];
          })(),
          ...((user && user?.groups?.includes('Azienda') && smUp && module(MODULE_FLAGS.COMMUNICATIONS)) ? [{
            title: t('notices'),
            path: paths.dashboard.notices,
            icon: ICONS.mail
          }] : []),
          ...((user && hasSupervisorPermission(user, 'expenses_view')
            && user?.data?.current_company && user?.data?.companies[user?.data?.current_company]?.feature_expense_reports) ? [{
            title: t('expense_reports'),
            path: paths.dashboard.expenseReports,
            icon: ICONS.invoice
          }] : []),
          ...(autoAvailable(user, ['Azienda']) ? [{ title: 'Auto', path: paths.dashboard.auto, icon: <Iconify icon="solar:wheel-bold-duotone" width={24} /> }] : []),
          ...(user && user?.groups?.includes('Azienda') && module(MODULE_FLAGS.DOCUMENTS) ? [{
            title: t('documents'),
            path: paths.dashboard.documents,
            icon: ICONS.file,
            info: (
              <>
                {pendingSignatureCount > 0 && (
                  <Label color="warning" variant="filled">
                    {pendingSignatureCount}
                  </Label>
                )}
                <HighlightParentDot ids={['invii-firme']} />
              </>
            ),
            badge: <HighlightParentDot ids={['invii-firme']} floating />,
            children: [
              {
                title: 'Documenti Azienda',
                path: paths.dashboard.documents,
              },
              ...((module(MODULE_FLAGS.DOCUMENT_GENERATION) || module(MODULE_FLAGS.SIGNATURES)) ? [{
                title: <span style={{ textTransform: 'none' }}>Invii e firme</span>,
                path: paths.dashboard.documentSignatures,
                info: (
                  <>
                    {pendingSignatureCount > 0 && (
                      <Label color="warning" variant="filled">
                        {pendingSignatureCount}
                      </Label>
                    )}
                    <HighlightDot id="invii-firme" />
                  </>
                ),
              }] : []),
              // Su richiesta: lo studio lo attiva con «Caricamento buste paga e dichiarazioni annuali».
              ...(currentCompany?.allows_company_upload_payslips === true ? [{
                title: 'Upload buste paga',
                path: paths.dashboard.uploadBustePaga,
              }] : []),
            ],
          }] : []),
          ...(user && user?.groups?.includes('Azienda') && module(MODULE_FLAGS.COMMUNICATIONS) ? [{
              title: t('letters'),
              path: paths.dashboard.letters,
              icon: <Iconify sx={{ width: 24, height: 24 }} icon="solar:chat-line-bold-duotone"/>,
            }] : []),
          ...(hasSupervisorPermission(user, 'scadenzario_access') && module(MODULE_FLAGS.DEADLINES) ? [{
            title: t('timetable'),
            path: paths.dashboard.calendar,
            icon: ICONS.calendar
          }] : []),

          {
            title: t('Supporto & News'),
            path: '#',
            icon: ICONS.chat,
            children: [
              ...((user && user?.groups?.includes('Azienda') && smUp) ? [
                  {
                    title: t('help_desk'),
                    path: paths.dashboard.helpDesk,
                  }
                ] : []
              ),
              ...((user && user?.groups?.includes('Azienda')) ? [
                  {
                    title: t('product_updates.label'),
                    path: paths.dashboard.news.root,
                  }
                ] : []
              ),
              {
                title: t('faq'),
                path: paths.dashboard.faq,
              }
            ]
          },

          ...((user && user?.groups?.includes('Azienda') && smUp && module(MODULE_FLAGS.PAYROLL_DATA)) ? [{
            title: t('dati contabili'),
            path: paths.dashboard.payrollData,
            icon: <Iconify sx={{ width: 24, height: 24 }} icon="solar:chart-bold-duotone"/>,
          }] : []),

          ...((user && user?.groups?.includes('Azienda') && smUp
            && preview(PREVIEW_FLAGS.LABOR_COST_BUDGET)) ? [{
            title: 'Budget costo lavoro',
            path: paths.dashboard.laborCostBudget,
            icon: <Iconify sx={{ width: 24, height: 24 }} icon="solar:wallet-money-bold-duotone"/>,
          }] : []),

          ...(hasSupervisorPermission(user, 'report_access') && module(MODULE_FLAGS.REPORTS) ? [
            {
              title: t('report'),
              path: '#',
              info: user?.groups?.includes('Azienda') && preview(PREVIEW_FLAGS.PAY_TRANSPARENCY)
                ? <HighlightParentDot ids={['trasparenza-retributiva']} />
                : undefined,
              badge: user?.groups?.includes('Azienda') && preview(PREVIEW_FLAGS.PAY_TRANSPARENCY)
                ? <HighlightParentDot ids={['trasparenza-retributiva']} floating />
                : undefined,
              icon: ICONS.analytics,
              children: [
                {
                  title: 'Panoramica',
                  path: paths.dashboard.reports,
                },

                ...((user && module(MODULE_FLAGS.TIMESHEET) && user?.data?.current_company && user?.data?.companies
                  && user?.data?.companies[user?.data?.current_company]?.timesheet_types?.some(hasTimesheetClockings)) ? [{
                  title: "Timbrature",
                  path: paths.dashboard.clockings,
                }] : []),

                ...(module(MODULE_FLAGS.TIMESHEET) ? [{
                  title: t('timesheet'),
                  path: paths.dashboard.stats,
                }] : []),

                ...((user && (user?.groups?.includes('Azienda') || user?.groups?.includes('Responsabile')) && smUp
                  && preview(PREVIEW_FLAGS.EMPLOYEES_RESIDUALS)) ? [{
                  title: "Residui Dipendenti",
                  path: paths.dashboard.employeesPayrollData,
                }] : []),

                ...((user && user?.groups?.includes('Azienda') && smUp) ? [{
                  title: "Utenti",
                  path: paths.dashboard.employeesSecurityReport,
                }] : []),

                ...((user && user?.groups?.includes('Azienda') && smUp && module(MODULE_FLAGS.DOCUMENTS)) ? [{
                  title: "Accesso documenti",
                  path: paths.dashboard.employeesDocuments,
                }] : []),

                ...((user && (user?.groups?.includes('Azienda') || user?.groups?.includes('Responsabile')) && smUp
                  && preview(PREVIEW_FLAGS.CUSTOM_REPORT)) ? [{
                  title: "Personalizzato",
                  path: paths.dashboard.employeesCustomReport,
                }] : []),

                ...((user && user?.groups?.includes('Azienda')
                  && preview(PREVIEW_FLAGS.PAY_TRANSPARENCY)) ? [{
                  title: 'Trasparenza retributiva',
                  path: paths.dashboard.payTransparency,
                  info: <HighlightDot id="trasparenza-retributiva" />,
                }] : []),

              ]
            }] : []),
          ...(smUp && user && user?.groups?.includes('Azienda') && links.length > 0 ? [{
            title: t('external_services'),
            path: '#/external',
            icon: ICONS.external,
            children: links.map((link) => (
              {
                title: link.description,
                path: link.url.startsWith('http') ? link.url : `https://${link.url}`,
                externalLink: true,
              }
            )),
          }] : []),
          ...((user && user?.groups?.includes('Azienda')) ? [{
            title: t('config'),
            path: paths.dashboard.config.root,
            icon: <Iconify sx={{ width: 24, height: 24 }} icon="solar:settings-bold-duotone"/>,
            children: [
              ...(smUp ? [{
                title: t('general'),
                path: paths.dashboard.config.root,
              }] : []),
              { title: t('custom_fields'), path: paths.dashboard.config.customFields },
              ...(smUp && module(MODULE_FLAGS.TIMESHEET) ? [{
                title: t('timesheet'),
                path: paths.dashboard.config.timesheet,
              }] : []),
              ...((user && user?.groups?.includes('Azienda') && smUp && module(MODULE_FLAGS.TIMESHEET)
                && user?.data?.current_company && user?.data?.companies[user?.data?.current_company]?.clocking_mode === 'qr') ? [{
                title: t('clocking places'),
                path: paths.dashboard.config.clockingPlaces,
              }] : []),
              // Solo le aziende che timbrano da file esterno hanno un connettore da seguire.
              ...(user?.data?.current_company && user?.data?.companies[user?.data?.current_company]?.clocking_mode === 'external' ? [{
                title: 'Connettore timbrature',
                path: paths.dashboard.config.clockingConnector,
              }] : []),
              ...((user && user?.groups?.includes('Azienda')
                && user?.data?.current_company && user?.data?.companies[user?.data?.current_company]?.feature_expense_reports) ? [{
                title: t('expense_reports'),
                path: paths.dashboard.config.expenseReports,
              }] : []),
              {
                title: t('supervisors'),
                path: paths.dashboard.config.supervisors,
              },
              ...(preview(PREVIEW_FLAGS.ORG_CHART) ? [{
                title: 'Organigramma',
                path: paths.dashboard.config.orgChart,
              }] : []),
              ...(smUp && module(MODULE_FLAGS.DOCUMENTS) && module(MODULE_FLAGS.DOCUMENT_GENERATION) ? [{
                title: t('document_templates'),
                path: paths.dashboard.config.documentTemplates,
              }] : []),
              ...((user?.data?.current_company && user?.data?.companies[user?.data?.current_company]?.feature_holiday_plan) ? [{
                title: t('holiday_plan'),
                path: paths.dashboard.config.holidayPlan,
              }] : []),
              ...(preview(PREVIEW_FLAGS.CONNECT_API) ? [{
                title: t('connect_api'),
                path: paths.dashboard.connectApi,
              }] : []),
            ],
          }] : []),
          // Responsabile con expenses_config: Configurazione con la sola voce Note spese.
          ...((user && !user?.groups?.includes('Azienda') && hasSupervisorPermission(user, 'expenses_config')
            && user?.data?.current_company && user?.data?.companies[user?.data?.current_company]?.feature_expense_reports) ? [{
            title: t('config'),
            path: paths.dashboard.config.expenseReports,
            icon: <Iconify sx={{ width: 24, height: 24 }} icon="solar:settings-bold-duotone"/>,
            children: [
              { title: t('expense_reports'), path: paths.dashboard.config.expenseReports },
            ],
          }] : []),
          ...(user && user?.groups?.includes('Azienda') ? [{
            title: t('your company'),
            path: paths.dashboard.company,
            icon: ICONS.job
          }] : []),
        ]
      }
    ],
    [t, links, user, pendingSignatureCount]
  );

  const data = getCompanyMenuVersion(currentCompany) === 2
    ? buildCompanyNavV2({
      user,
      t,
      smUp,
      links,
      pendingSignatureCount,
    })
    : legacyData;

  return onboarding ? lockNavForOnboarding(data, user.data.current_company) : data;
}
